import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslations } from '@core/js/hooks/i18n';
import { useShowManagedPopup } from '@core/js/hooks/useShowManagedPopup';

import { showLoginExtendedErrorPopup } from '@interlayer/js/app/components/main/Login/actions';
import { getContactPreferencesForFront } from '@interlayer/js/app/components/main/MarketingPreferences/helpers';
import { TUpdatePreferencesStepData } from '@interlayer/js/app/components/main/Registration/components/templates/OnlineReg/components/RegInSteps/components/upgrade/types';
import {
    DEFAULT_RETAIL_SUSPENSION_LOGIN_ARTICLE,
    DEFAULT_RETAIL_SUSPENSION_TAG_NAME
} from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/components/slotUpgrade/constants';
import { showRetailAlreadySlotClubMemberPopupAction } from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/components/slotUpgrade/steps/LoginStep/components/AlreadySlotClubMemberPopup/actions';
import { useCheckSlotClubOptedInPlayerTags } from '@interlayer/js/app/components/main/UserDetails/components/MarketingPreferences/hooks/useSlotClubOptedInPlayerTags';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useAllPlayerTags } from '@interlayer/js/app/hooks/user/hooks/useAllPlayerTags';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import {
    setRegistrationNameStep,
    setSlotUpgradeData } from '@interlayer/js/app/store/reducers/Registration/actions';
import { STEP_NAMES } from '@interlayer/js/app/store/reducers/Registration/constants';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useLoginBySessionToken } from '@interlayer/js/hooks/auth/hooks/useLoginBySessionToken';
import { useLogout } from '@interlayer/js/hooks/auth/hooks/useLogout';
import { useFetchUserDetails } from '@interlayer/js/hooks/user/hooks/useFetchUserDetails';
import { IPlayerTag } from '@interlayer/js/hooks/user/store/buzz/types';

export const useContinueSlotLightLogin = () => {
    const logout = useLogout();
    const dispatch = useDispatch();
    const fetchUserDetails = useFetchUserDetails();
    const loginBySessionToken = useLoginBySessionToken();
    const translate = useTranslations('playtech.mobile.system.login.');
    const loginExtendedErrorPopup = useShowManagedPopup(showLoginExtendedErrorPopup);
    const checkSlotClubOptedInPlayerTags = useCheckSlotClubOptedInPlayerTags();
    const { fetch: fetchAllPlayerTags } = useAllPlayerTags({ isForceFetch: true, isInitFetch: false });
    const [
        dTagRetailSuspensionTagName = DEFAULT_RETAIL_SUSPENSION_TAG_NAME,
        dTagLoginRetailSuspensionArticleId = DEFAULT_RETAIL_SUSPENSION_LOGIN_ARTICLE
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.retailSuspensionTagName,
        DYNAMIC_TAG_NAMES.loginRetailSuspensionArticleId
    ]);

    return useCallback(({ userName, sessionToken }: { userName: string; sessionToken: string }) => {
        return loginBySessionToken(userName, sessionToken)
            .then(() => fetchAllPlayerTags({ isUpdate: true }))
            .then((playerTags?: Record<string, IPlayerTag>) => {
                if (!playerTags) {
                    return Promise.reject();
                }

                const retailSuspensionPlayerTag = playerTags[dTagRetailSuspensionTagName];
                const isSlotClubOptedInPlayerTag = Boolean(checkSlotClubOptedInPlayerTags(playerTags).length);

                if (retailSuspensionPlayerTag || isSlotClubOptedInPlayerTag) {
                    if (retailSuspensionPlayerTag) {
                        loginExtendedErrorPopup({
                            expirationDate: retailSuspensionPlayerTag.expirationDate,
                            articleId: dTagLoginRetailSuspensionArticleId,
                            title: translate('extended.title', [userName]),
                            onlyArticle: false,
                            withLogo: false
                        });

                        logout(true);
                    } else {
                        dispatch(showRetailAlreadySlotClubMemberPopupAction({ userName }));
                    }

                    return Promise.reject();
                }
            })
            .then(fetchUserDetails)
            .then(({
                phone,
                email,
                cellphone = phone,
                contactPreferences
            }) => {
                dispatch(setSlotUpgradeData({
                    stepName: STEP_NAMES.PREFERENCES_SLOT_UPGRADE_STEP,
                    data: {
                        ...getContactPreferencesForFront(contactPreferences) as TUpdatePreferencesStepData,
                        [VALIDATION_FIELDS.email]: email,
                        [VALIDATION_FIELDS.phone]: cellphone
                    }
                }));

                dispatch(setRegistrationNameStep(STEP_NAMES.PREFERENCES_SLOT_UPGRADE_STEP));
            });
    }, [
        checkSlotClubOptedInPlayerTags, dTagLoginRetailSuspensionArticleId, dTagRetailSuspensionTagName, dispatch,
        fetchAllPlayerTags, fetchUserDetails, loginBySessionToken, loginExtendedErrorPopup, logout, translate
    ]);
};
